import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';
import SpineModelViewer from '../components/SpineModelViewer';
import CTSliceViewer from '../components/CTSliceViewer';
import SagittalMRIViewer from '../components/SagittalMRIViewer';
import AxialMRIViewer from '../components/AxialMRIViewer';
import { useLocalStorage } from '../utils/hooks';
import { peekAutoSavePending, clearAutoSavePending, autoSaveStudy } from '../services/studySync';
import ctVolumePreloader from '../services/ctVolumePreloader';
import AiSummaryModal from '../components/AiSummaryModal';
import API_URL from '../config/api';

// Vertebral levels for data fetching
const vertebralLevels = [
  'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7',
  'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12',
  'L1', 'L2', 'L3', 'L4', 'L5', 'S1'
];

// Default structure for spine measurements
const createDefaultSpineData = (level) => ({
  level,
  pedicleWidthR: "...",
  pedicleWidthL: "...",
  vertebralHeight: "...",
  vertebralWidth: "...",
  vertebralDepth: "...",
  interWidthL: level === 'S1' ? '' : `${level}-${getNextLevel(level)}`,
  interHeight: level === 'S1' ? '' : "...",
  interAngle: level === 'S1' ? '' : "...",
  isLoading: true,
  hasError: false
});

// Helper function to get next vertebral level
function getNextLevel(level) {
  const currentIndex = vertebralLevels.indexOf(level);
  return currentIndex < vertebralLevels.length - 1 ? vertebralLevels[currentIndex + 1] : '';
}

// Disc levels for MRI analysis
const discLevels = [
  "C3-C4", "C4-C5", "C5-C6", "C6-C7", "C7-T1",
  "T1-T2", "T2-T3", "T3-T4", "T4-T5", "T5-T6", "T6-T7", "T7-T8", "T8-T9", "T9-T10", "T10-T11", "T11-T12", "T12-L1",
  "L1-L2", "L2-L3", "L3-L4", "L4-L5", "L5-S1"
];

// Sample MRI analysis data
// Sample MRI analysis data removed - using dynamic state
// const mriAnalysisData = { ... };


function AnatomicalMeasurementsPage() {
  const location = useLocation();
  const [selectedRow, setSelectedRow] = useState(null);
  // useLocalStorage enabled for persistence across pages
  const [selectedRows, setSelectedRows] = useLocalStorage('anatomical-selected-rows', []);
  // Concerning/Herniated rows will be populated from fetched data
  const [concerningRows, setConcerningRows] = useLocalStorage('anatomical-concerning-rows', []);
  const [herniatedRows, setHerniatedRows] = useLocalStorage('anatomical-herniated-rows', []);
  const [selectedRegions, setSelectedRegions] = useState({
    cervical: true,
    thoracic: true,
    lumbar: true,
    sacral: true
  });
  const [viewMode, setViewMode] = useState('3D');
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [mriModalOpen, setMriModalOpen] = useState(false);
  const [ctViewType, setCtViewType] = useState('Axial'); // Axial, Sagittal, or Coronal
  const [selectedDiscLevel, setSelectedDiscLevel] = useState(null);
  const [viewTargetLevel, setViewTargetLevel] = useState(null); // Decoupled target for viewer

  // State for dynamic IVD levels from backend
  const [detectedIvdLevels, setDetectedIvdLevels] = useState([]);
  const [ivdMapping, setIvdMapping] = useState({});
  const [useDetectedLevels, setUseDetectedLevels] = useState(false);
  const [currentAxialSlice, setCurrentAxialSlice] = useState(null);

  // State for MRI Analysis Data
  const [mriAnalysisData, setMriAnalysisData] = useState({});

  // Direction-aware auto-hiding table header: scrolling down lets the header
  // scroll away with the data; any upward scroll slides it back in.
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollTopRef = React.useRef(0);
  const handleTableScroll = useCallback((e) => {
    const st = e.currentTarget.scrollTop;
    const last = lastScrollTopRef.current;
    if (st <= 8) {
      setHeaderHidden(false);
    } else if (st > last + 4) {
      setHeaderHidden(true);
    } else if (st < last - 4) {
      setHeaderHidden(false);
    }
    lastScrollTopRef.current = st;
  }, []);

  // Initial load: Fetch all persisted axial measurements
  useEffect(() => {
    const fetchPersistedMeasurements = async () => {
      try {
        console.log("Fetching persisted axial measurements...");
        const response = await api.get('/inference/axial-measurements');
        if (response.status === 200) {
          const data = response.data;
          if (data && Object.keys(data).length > 0) {
            console.log("Loaded persisted measurements:", Object.keys(data));

            // Normalize and flatten the data структура
            const normalizedData = {};
            Object.entries(data).forEach(([level, levelData]) => {
              if (levelData && levelData.measurements) {
                const flattened = {};
                for (const [key, val] of Object.entries(levelData.measurements)) {
                  if (val && typeof val === 'object' && 'value' in val) {
                    flattened[key] = val.value;
                  } else {
                    flattened[key] = val;
                  }
                }
                normalizedData[level] = flattened;
              }
            });

            setMriAnalysisData(normalizedData);
          }
        }
      } catch (error) {
        console.error("Error fetching persisted measurements:", error);
      }
    };

    fetchPersistedMeasurements();
  }, []);

  // Fetch MRI measurements when level changes
  useEffect(() => {
    const fetchMriData = async () => {
      if (!selectedDiscLevel || !mriModalOpen) return;

      // If we already have data for this level, don't re-fetch unless force refresh needed
      if (mriAnalysisData[selectedDiscLevel]) return;

      try {
        console.log(`Fetching MRI analysis for ${selectedDiscLevel}...`);
        const response = await api.post('/inference/axial-measurements', { level: selectedDiscLevel });

        if (response.status === 200) {
          const data = response.data;
          if (data.success && data.measurements) {
            // Flatten measurements if they have value/unit structure
            const flattened = {};
            for (const [key, val] of Object.entries(data.measurements)) {
              if (val && typeof val === 'object' && 'value' in val) {
                flattened[key] = val.value;
              } else {
                flattened[key] = val;
              }
            }

            setMriAnalysisData(prev => ({
              ...prev,
              [selectedDiscLevel]: flattened
            }));
          } else {
            console.warn("MRI Analysis failed or returned no data:", data);
          }
        } else {
          console.error("Failed to fetch MRI analysis:", response.status);
        }
      } catch (error) {
        console.error("Error fetching MRI analysis:", error);
      } finally {
        // loading state removed
      }
    };

    fetchMriData();
  }, [selectedDiscLevel, mriModalOpen, mriAnalysisData]);

  // Update concerning rows and herniated rows based on MRI analysis findings
  useEffect(() => {
    if (!mriAnalysisData || Object.keys(mriAnalysisData).length === 0) return;

    // Calculate new concerning and herniated levels based on all available analysis data
    const newConcerningLevels = new Set(concerningRows);
    const newHerniatedLevels = new Set(herniatedRows);

    let hasConcerningChanges = false;
    let hasHerniatedChanges = false;

    Object.entries(mriAnalysisData).forEach(([level, data]) => {
      if (!data) return;

      const levels = level.split('-');

      // Check for stenosis indication (Concerning - Red)
      if (data.stenosis === true) {
        levels.forEach(l => {
          if (!newConcerningLevels.has(l)) {
            newConcerningLevels.add(l);
            hasConcerningChanges = true;
          }
        });
      }

      // Check for herniation (Herniated - Orange)
      // We consider Moderate or Severe as herniated for visualization
      if (data.herniation === 'Moderate' || data.herniation === 'Severe') {
        levels.forEach(l => {
          if (!newHerniatedLevels.has(l)) {
            newHerniatedLevels.add(l);
            hasHerniatedChanges = true;
          }
        });
      }
    });

    if (hasConcerningChanges) {
      console.log('Updating concerning levels based on MRI analysis:', Array.from(newConcerningLevels));
      setConcerningRows(Array.from(newConcerningLevels));
    }

    if (hasHerniatedChanges) {
      console.log('Updating herniated levels based on MRI analysis:', Array.from(newHerniatedLevels));
      setHerniatedRows(Array.from(newHerniatedLevels));
    }
  }, [mriAnalysisData, concerningRows, herniatedRows]);


  // Check if user is coming from Upload page
  const isFromUploadPage = location.state?.fromUploadPage === true;

  // Check for cached measurements in sessionStorage
  const getCachedMeasurements = () => {
    const cached = sessionStorage.getItem('ctMeasurements');
    if (cached) {
      try {
        const parsedData = JSON.parse(cached);
        console.log('Loading measurements from cache');
        return parsedData;
      } catch (e) {
        console.error('Failed to parse cached measurements:', e);
      }
    }
    return null;
  };

  // CT images will be loaded on demand by CTSliceViewer
  // No need to preload here anymore

  // Initialize spine data with cached data if available
  const initializeSpineData = () => {
    // If coming from upload page, always start with empty data
    if (isFromUploadPage) {
      return {
        spineData: vertebralLevels.map(level => createDefaultSpineData(level)),
        loadedLevels: new Set()
      };
    }

    // Otherwise, check for cached data
    const cachedData = getCachedMeasurements();
    if (cachedData && cachedData.measurements) {
      // Use cached data
      const loadedSet = new Set(cachedData.measurements.map(m => m.level));
      return {
        spineData: vertebralLevels.map(level => {
          const cached = cachedData.measurements.find(m => m.level === level);
          return cached || createDefaultSpineData(level);
        }),
        loadedLevels: loadedSet
      };
    }
    // No cache, use default
    return {
      spineData: vertebralLevels.map(level => createDefaultSpineData(level)),
      loadedLevels: new Set()
    };
  };

  // States for CT measurements data
  const initialData = initializeSpineData();
  const [spineData, setSpineData] = useState(initialData.spineData);
  const [measurementsLoading, setMeasurementsLoading] = useState(isFromUploadPage);
  const [loadingStatus, setLoadingStatus] = useState({ step: 0, total: 9, action: 'Initializing...' });
  const [loadedLevels, setLoadedLevels] = useState(initialData.loadedLevels);

  // Function to fetch measurements for all levels - now called only when pipeline is complete
  const fetchAllMeasurements = async () => {
    console.log('Fetching all completed measurements...');

    try {
      const response = await api.get('/ct-measurements/all');
      if (response.status === 200) {
        const allData = response.data;
        const allMeasurements = [];

        for (const level of vertebralLevels) {
          const data = allData[level];
          if (data) {
            const processedLevelData = {
              level,
              pedicleWidthR: data?.Pedicles?.right?.["Measurement Data"]?.minor_2D_length
                ? `${data.Pedicles.right["Measurement Data"].minor_2D_length.toFixed(1)}`
                : 'N/A',
              pedicleWidthL: data?.Pedicles?.left?.["Measurement Data"]?.minor_2D_length
                ? `${data.Pedicles.left["Measurement Data"].minor_2D_length.toFixed(1)}`
                : 'N/A',
              vertebralHeight: (() => {
                const anterior = data?.Vertebral_Body?.["Measurement Data"]?.anterior_height_distance;
                const posterior = data?.Vertebral_Body?.["Measurement Data"]?.posterior_height_distance;
                if (anterior && posterior) {
                  return `${((anterior + posterior) / 2).toFixed(1)}`;
                } else if (anterior) {
                  return `${anterior.toFixed(1)}`;
                } else if (posterior) {
                  return `${posterior.toFixed(1)}`;
                }
                return 'N/A';
              })(),
              vertebralWidth: data?.Vertebral_Body?.["Measurement Data"]?.vertebral_width
                ? `${data.Vertebral_Body["Measurement Data"].vertebral_width.toFixed(1)}`
                : 'N/A',
              vertebralDepth: data?.Vertebral_Body?.["Measurement Data"]?.vertebral_depth
                ? `${data.Vertebral_Body["Measurement Data"].vertebral_depth.toFixed(1)}`
                : 'N/A',
              interWidthL: level === 'S1' ? '' : `${level}-${getNextLevel(level)}`,
              interHeight: data?.Discs?.Inferior?.["Measurement Data"]?.disc_centroid_height
                ? `${data.Discs.Inferior["Measurement Data"].disc_centroid_height.toFixed(1)}`
                : (level === 'S1' ? '' : 'N/A'),
              interAngle: data?.Discs?.Inferior?.["Measurement Data"]?.disc_angle_degrees
                ? `${data.Discs.Inferior["Measurement Data"].disc_angle_degrees.toFixed(1)}°`
                : (level === 'S1' ? '' : 'N/A'),
              isLoading: false,
              hasError: false,
              rawData: data
            };
            allMeasurements.push(processedLevelData);
          }
        }

        if (allMeasurements.length > 0) {
          setSpineData(prevData => {
            const newData = [...prevData];
            allMeasurements.forEach(measurement => {
              const index = newData.findIndex(item => item.level === measurement.level);
              if (index !== -1) {
                newData[index] = measurement;
              }
            });
            return newData;
          });

          setLoadedLevels(prev => {
            const newSet = new Set(prev);
            allMeasurements.forEach(m => newSet.add(m.level));
            return newSet;
          });

          sessionStorage.setItem('ctMeasurements', JSON.stringify({
            measurements: allMeasurements,
            timestamp: Date.now()
          }));
        }

        return allMeasurements.length;
      }
    } catch (error) {
      console.error('Error fetching bulk measurements:', error);
    }
    return 0;
  };

  // Refs to track state inside intervals (avoiding stale closures)
  const measurementsLoadingRef = React.useRef(measurementsLoading);
  const loadedLevelsRef = React.useRef(loadedLevels);
  // Ref to track if we've checked for persisted data
  const persistenceCheckedRef = React.useRef(false);
  // Ref to track if we've triggered the calculation
  const triggerAttemptedRef = React.useRef(false);

  // ---- Automatic study save (set up by UploadPage when processing ends) ----
  // The pending mark survives navigation; it is cleared only when the save
  // actually fires, so leaving this page early just defers the save to the
  // next visit. Restored studies never set the mark.
  const autoSaveFiredRef = React.useRef(false);
  const [autoSaveNotice, setAutoSaveNotice] = useState(null); // 'saved' | 'error' | null

  const runAutoSave = React.useCallback(async () => {
    if (autoSaveFiredRef.current) return;
    autoSaveFiredRef.current = true;
    const result = await autoSaveStudy();
    if (result.saved || result.unsupported) {
      // Only a save that actually happened (or can never happen) consumes the
      // mark — clearing before the request meant a transient failure silently
      // cancelled the auto-save forever.
      clearAutoSavePending();
    }
    if (result.saved) {
      setAutoSaveNotice('saved');
      setTimeout(() => setAutoSaveNotice(null), 5000);
    } else if (!result.unsupported) {
      autoSaveFiredRef.current = false;
      setAutoSaveNotice('error');
      setTimeout(() => setAutoSaveNotice(null), 8000);
    }
  }, []);

  // Scans without a CT measurement pipeline are saved on arrival; CT scans
  // are saved from the measurement-status poll once results are in.
  useEffect(() => {
    const pending = peekAutoSavePending();
    if (pending && !pending.awaitMeasurements) {
      runAutoSave();
    }
  }, [runAutoSave]);

  // Sync refs with state
  useEffect(() => {
    measurementsLoadingRef.current = measurementsLoading;
  }, [measurementsLoading]);

  useEffect(() => {
    loadedLevelsRef.current = loadedLevels;
  }, [loadedLevels]);

  // Effect to manage measurement pipeline
  useEffect(() => {
    let pollInterval;
    // Polling reschedules itself, so stopping needs an explicit flag — clearing
    // the timer alone would be undone by the next scheduleNext().
    let polling = true;
    const stopPolling = () => {
      polling = false;
      clearTimeout(pollInterval);
    };

    const checkStatus = async () => {
      try {
        console.log('Polling measurement status...');
        const response = await api.get('/inference/measurement-status');
        if (response.status !== 200) throw new Error('Status fetch failed');

        const statusData = response.data;
        const currentLoading = measurementsLoadingRef.current;
        const currentLoadedLevels = loadedLevelsRef.current;

        // console.log('Status received:', statusData);

        if (statusData.status === 'running') {
          if (!currentLoading) setMeasurementsLoading(true);
          setLoadingStatus({
            step: statusData.current_step,
            total: statusData.total_steps,
            action: statusData.current_action
          });
        } else if (statusData.status === 'completed') {
          // Pipeline finished. Ensure we fetch data.
          // We fetch if we are loading OR if we don't have full data yet.
          const shouldFetch = currentLoading || currentLoadedLevels.size < vertebralLevels.length;

          let gotData = currentLoadedLevels.size > 0;
          if (shouldFetch) {
            console.log('Pipeline completed, fetching results...');
            const fetchedCount = await fetchAllMeasurements();
            gotData = gotData || fetchedCount > 0;
            setMeasurementsLoading(false);
          }

          // 'completed' with no data on a fresh upload is a stale status from
          // a previous scan (the flag is an in-memory global, and in the split
          // CPU/GPU topology /clear-inputs resets it on a different container
          // than the one answering this poll). Trigger the pipeline instead of
          // trusting it — and don't auto-save an empty study.
          if (isFromUploadPage && !gotData && !triggerAttemptedRef.current) {
            triggerAttemptedRef.current = true;
            triggerCalculation();
          } else if (peekAutoSavePending()?.awaitMeasurements) {
            // Measurement pipeline done — save the study automatically if the
            // upload pipeline requested it. Whatever the backend produced (even
            // a partial run) is the scan's state; the save gathers server-side.
            runAutoSave();
          }

          // If we have data and are just polling, stop polling
          if (currentLoadedLevels.size >= vertebralLevels.length) {
            stopPolling();
          }
        } else if (statusData.status === 'error') {
          setMeasurementsLoading(false);
          console.error('Measurement Status Error:', statusData.error);
          stopPolling();
          // The scan's segmentation/images may still be valid even though the
          // measurement pipeline failed — save what exists rather than leaving
          // the pending mark stranded (it would mis-fire on a later visit).
          if (peekAutoSavePending()) {
            runAutoSave();
          }
        } else if (statusData.status === 'idle') {
          // 1. If idle, check if we currently have no data. If so, try to load from disk (persistence).
          // preventing spam with persistenceCheckedRef
          if (!isFromUploadPage && !currentLoading && currentLoadedLevels.size === 0 && !persistenceCheckedRef.current) {
            console.log('Pipeline idle, checking for persisted measurements...');
            persistenceCheckedRef.current = true; // Mark as checked so we don't spam
            await fetchAllMeasurements();
          }

          // 2. Not running. If we came from upload, we expect it to run. trigger it.
          // We check !triggerAttemptedRef.current to avoid spamming the backend
          if (isFromUploadPage && currentLoadedLevels.size < vertebralLevels.length && !triggerAttemptedRef.current) {
            triggerAttemptedRef.current = true;
            triggerCalculation();
          }
        }
      } catch (e) {
        console.error("Error polling status:", e);
      }
    };

    const triggerCalculation = async () => {
      if (isFromUploadPage) {
        try {
          console.log('Triggering measurement calculations...');
          setMeasurementsLoading(true);
          // Trigger matches the backend signature for background task
          await api.post(`${API_URL}/inference/calculate-measurements`);
        } catch (e) {
          // Ignore if already running
          if (e.response && e.response.status !== 400) {
            console.error("Error triggering calculations:", e);
          }
        }
      }
    }

    // Poll quickly at first, then ease off. The measurement pipeline runs for
    // a couple of minutes, and a flat 1s interval fired 120+ requests at the
    // GPU container for a job whose status barely changes after the first few
    // seconds. Backs off 1s -> 5s, so a fast finish is still noticed promptly.
    let pollDelay = 1000;
    const MAX_POLL_DELAY = 5000;

    const scheduleNext = () => {
      if (!polling) return;
      pollInterval = setTimeout(async () => {
        await checkStatus();
        if (!polling) return;   // checkStatus may have decided we're done
        pollDelay = Math.min(pollDelay * 1.5, MAX_POLL_DELAY);
        scheduleNext();
      }, pollDelay);
    };

    checkStatus(); // Initial check
    scheduleNext();

    return stopPolling;
  }, [isFromUploadPage]); // Only run on mount

  // Clean up unused functions and effects
  // (We replaced the old useEffect and fetch functions with the unified one above)

  // Start preloading CT volumes in the background
  useEffect(() => {
    ctVolumePreloader.startPreloading().catch(err => {
      console.error('Background CT preloading failed:', err);
    });
  }, []);

  // Fetch IVD levels from backend for MRI Analysis
  useEffect(() => {
    const fetchIvdLevels = async () => {
      try {
        const response = await api.get(`${API_URL}/mri/ivd-levels`);
        const data = response.data;

        if (data.status === 'success' && data.levels && data.levels.length > 0) {
          setDetectedIvdLevels(data.levels);
          setIvdMapping(data.ivd_mapping || {});
          setUseDetectedLevels(true);

          // Set initial selected level to first detected level
          if (data.levels.length > 0) {
            setSelectedDiscLevel(data.levels[0]);
            setViewTargetLevel(data.levels[0]);
          }

          console.log('Loaded detected IVD levels:', data.levels);
        } else {
          console.log('No detected IVD levels, using defaults');
          setUseDetectedLevels(false);
        }
      } catch (error) {
        console.log('Could not fetch IVD levels, using defaults:', error.message);
        setUseDetectedLevels(false);
      }
    };

    fetchIvdLevels();
  }, []);

  // Close MRI level dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('mri-level-dropdown-content');
      if (dropdown && !event.target.closest('#mri-level-dropdown-container')) {
        dropdown.style.display = 'none';
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get disc levels for dropdown - use detected levels if available, otherwise defaults
  // Reverse order so L5-S1 is at bottom (anatomically correct - inferior to superior)
  const getDiscLevelsForDropdown = () => {
    if (useDetectedLevels && detectedIvdLevels.length > 0) {
      return [...detectedIvdLevels].reverse();
    }
    return discLevels;
  };

  // Handle sagittal centroid click - sync with axial viewer
  const handleSagittalCentroidClick = (discLevel) => {
    setSelectedDiscLevel(discLevel);
    setViewTargetLevel(discLevel); // Explicit click should jump viewer
  };

  // Handle axial slice change from AxialMRIViewer
  // Updates currentAxialSlice always (for crosshair sync)
  // Updates selectedDiscLevel based on "sticky" logic: largest IVD slice <= current slice
  // Wrapped in useCallback to prevent unnecessary re-renders in child component
  const handleAxialSliceChange = useCallback((sliceIndex, ivdLevels) => {
    setCurrentAxialSlice(sliceIndex);

    // 1. Explicit IVD levels (Exact Match Trigger from Viewer)
    if (ivdLevels && ivdLevels.length > 0) {
      const level = ivdLevels[0].replace('IVD_', '').replace('/', '-');
      setSelectedDiscLevel(level);
      return;
    }

    // 2. Stateful Hysteresis Logic
    if (ivdMapping && Object.keys(ivdMapping).length > 0) {
      // Normalize and sort IVD data
      const sortedIvds = Object.entries(ivdMapping)
        .map(([name, info]) => ({
          name: name.replace('IVD_', '').replace('/', '-'),
          slice: typeof info === 'object' ? info.axial_slice : info
        }))
        .filter(item => item.slice !== undefined)
        .sort((a, b) => a.slice - b.slice);

      if (sortedIvds.length > 0) {
        // A. Exact Match Check (Fallback)
        const exact = sortedIvds.find(i => i.slice === sliceIndex);
        if (exact) {
          setSelectedDiscLevel(exact.name);
          return;
        }

        // B. Find Bracket [Floor, Ceil] (Closest IVDs above and below)
        let floor = null;
        let ceil = null;
        for (const item of sortedIvds) {
          if (item.slice < sliceIndex) floor = item;
          else if (item.slice > sliceIndex) { ceil = item; break; }
        }

        // C. Sticky Hysteresis: Keep current if it matches valid neighbor
        const current = selectedDiscLevel;
        if ((floor && current === floor.name) || (ceil && current === ceil.name)) {
          return; // Stay on current level
        }

        // D. Default to Floor (previous level)
        if (floor) setSelectedDiscLevel(floor.name);
        else if (ceil) setSelectedDiscLevel(ceil.name);
      }
    }
  }, [ivdMapping, selectedDiscLevel]);

  // Handle row click with multi-selection support and deselection
  const handleRowClick = (level, event) => {
    // Prevent default behavior to avoid text selection
    if (event) {
      event.preventDefault();
    }

    // Check if Shift key is pressed for range selection - Finder-like behavior
    if (event && event.shiftKey && selectedRows.length > 0) {
      // Get the last selected row as anchor for Finder-like behavior
      const lastSelectedRow = selectedRows[selectedRows.length - 1];

      // Find indices of anchor and current level
      const spineDataLevels = spineData.map(item => item.level);
      const anchorIndex = spineDataLevels.indexOf(lastSelectedRow);
      const currentIndex = spineDataLevels.indexOf(level);

      // Determine range of selection
      const startIdx = Math.min(anchorIndex, currentIndex);
      const endIdx = Math.max(anchorIndex, currentIndex);

      // Get all levels in the range
      const rangeSelection = spineDataLevels.slice(startIdx, endIdx + 1);

      // For Command+Shift+Click (like in Finder), extend the current selection with the range
      if (event.metaKey || event.ctrlKey) {
        // Combine existing selection with new range, removing duplicates
        const combinedSelection = [...new Set([...selectedRows, ...rangeSelection])];
        setSelectedRows(combinedSelection);
      } else {
        // Regular Shift+Click just selects the range
        setSelectedRows(rangeSelection);
      }

      // Update the last selected row
      setSelectedRow(level);
    }
    // Check if Ctrl/Cmd key is pressed for toggling selection - Finder-like behavior
    else if (event && (event.ctrlKey || event.metaKey)) {
      // If already selected, remove it, otherwise add it
      setSelectedRows(prev => {
        if (prev.includes(level)) {
          return prev.filter(item => item !== level);
        } else {
          return [...prev, level];
        }
      });

      // Update the last selected row
      setSelectedRow(level);
    }
    // Regular click - select single item
    else {
      // If clicking on already selected row, deselect it
      if (selectedRows.length === 1 && selectedRows[0] === level) {
        setSelectedRows([]);
        setSelectedRow(null);
      } else {
        // Otherwise select just this row
        setSelectedRows([level]);
        setSelectedRow(level);
      }
    }
  };

  const handleRegionToggle = (region) => {
    // Check if this would result in all regions being hidden
    const wouldAllBeHidden = Object.entries(selectedRegions)
      .filter(([key]) => key !== region) // Exclude the region we're toggling
      .every(([_, isSelected]) => !isSelected) && selectedRegions[region]; // All others are false and this one is true (about to be toggled off)

    // Only allow toggling if it won't hide all regions
    if (!wouldAllBeHidden) {
      setSelectedRegions(prev => ({
        ...prev,
        [region]: !prev[region]
      }));
    }
  };

  const toggleAiSummary = () => {
    setAiSummaryOpen(!aiSummaryOpen);
  };

  // Helper to determine if a value should be highlighted (red) - only pedicle width < 4mm
  const isHighlighted = (level, column, value) => {
    // Parse numeric value if it contains numbers
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return false;

    // Only highlight pedicle width measurements that are less than 4mm
    if (column === 'pedicleWidthR' || column === 'pedicleWidthL') {
      return numericValue < 4.0;
    }

    return false;
  };

  // Helper to determine if a vertebra has any concerning measurements
  // This function is for future expansion, right now we're using a fixed array

  // Calculate loading progress


  return (
    <div style={styles.container}>
      <div className="fade-in" style={styles.dashboard}>
        {/* Left Section - Spine Model & Controls */}
        <div style={styles.leftSection}>
          {/* Viewing Mode Selector */}
          <div className="card card--tight" style={styles.viewingCard}>
            <div className="row row--between" style={styles.viewingHead}>
              <span style={styles.panelLabel}>Viewing</span>
              <span className="badge badge--accent">
                {viewMode} {selectedRow ? `- ${selectedRow}` : ''}
              </span>
            </div>
            <div className="seg" style={styles.segFull}>
              {['3D', 'CT'].map(mode => (
                <button
                  key={mode}
                  type="button"
                  className={`seg__opt${viewMode === mode ? ' seg__opt--active' : ''}`}
                  style={styles.segOptFlex}
                  onClick={async () => {
                    setViewMode(mode);
                    // No need to preload all slices - just use smart caching
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic Viewer based on viewMode */}
          <div className="viewer" style={styles.viewerBox}>
            {viewMode === '3D' ? (
              <SpineModelViewer
                selectedLevel={selectedRow}
                selectedLevels={selectedRows}
                concerningLevels={concerningRows}
                herniatedLevels={herniatedRows}
                spineOptions={selectedRegions}
              />
            ) : viewMode === 'CT' ? (
              <CTSliceViewer
                key={ctViewType} // Force remount when view type changes
                viewType={ctViewType}
                selectedLevel={selectedRow}
                showOverlay={true}
              />
            ) : null}
          </div>

          {/* Region/View Type Selection - Conditional based on viewMode */}
          <div className="card card--tight" style={styles.regionCard}>
            {viewMode === '3D' ? (
              // Show region buttons for 3D view
              Object.entries(selectedRegions).map(([region, isSelected]) => {
                // Check if this is the only active region (can't be toggled off)
                const isOnlyActiveRegion = isSelected &&
                  Object.entries(selectedRegions)
                    .filter(([key]) => key !== region)
                    .every(([_, selected]) => !selected);

                return (
                  <button
                    key={region}
                    type="button"
                    className={`btn btn--sm ${isSelected ? 'btn--secondary' : 'btn--ghost'}`}
                    style={{
                      ...styles.regionButton,
                      ...(isSelected ? styles.regionButtonActive : {}),
                      ...(isOnlyActiveRegion ? styles.regionButtonRequired : {})
                    }}
                    onClick={() => handleRegionToggle(region)}
                    title={isOnlyActiveRegion ? "At least one region must be visible" : ""}
                  >
                    {region.charAt(0).toUpperCase() + region.slice(1)}
                  </button>
                );
              })
            ) : viewMode === 'CT' ? (
              // Show view type buttons for CT view
              <div className="seg" style={styles.segFull}>
                {['Axial', 'Sagittal', 'Coronal'].map(type => (
                  <button
                    key={type}
                    type="button"
                    className={`seg__opt${ctViewType === type ? ' seg__opt--active' : ''}`}
                    style={styles.segOptFlex}
                    onClick={async () => {
                      setCtViewType(type);
                      // No need to preload all - smart caching will handle it
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>


        {/* Right Section - Data Table & Toolbar */}
        <div style={styles.rightSection}>
          {/* Toolbar */}
          <div className="row row--center" style={styles.toolbar}>
            {/* MRI button moved from viewing mode */}
            <button
              className="btn btn--secondary"
              onClick={() => setMriModalOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>MRI Analysis</span>
            </button>
            <button
              className="btn btn--secondary"
              onClick={toggleAiSummary}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>AI Summary</span>
            </button>
            <button
              className="btn btn--danger"
              onClick={() => {
                // Clear all selected rows
                setSelectedRows([]);
                setSelectedRow(null);

                // Provide visual feedback
                console.log('Cleared all selected vertebrae');
              }}
              disabled={selectedRows.length === 0}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Clear Selection</span>
            </button>

            {/* Multi-selection info */}
            <div className="badge badge--neutral" style={styles.selectionInfo}>
              <span
                className="status-dot"
                style={{
                  backgroundColor: selectedRows.length > 1 ? 'var(--accent)' : 'rgba(255,255,255,0.25)'
                }}
              ></span>
              <span style={styles.selectionInfoText}>
                {selectedRows.length > 1
                  ? `${selectedRows.length} levels selected`
                  : 'Shift+Click for range, Cmd/Ctrl+Click to toggle'}
              </span>
            </div>
          </div>

          {/* AI Summary Modal */}
          <AiSummaryModal
            isOpen={aiSummaryOpen}
            onClose={() => setAiSummaryOpen(false)}
          />

          {/* Data Table */}
          <div className="table-wrap" style={styles.tableScroller} onScroll={handleTableScroll}>
            <table
              className={`table mtable${headerHidden ? ' mtable--hidden' : ''}`}
              style={styles.measurementTable}
            >
              <thead>
                <tr>
                  {/* Level Header */}
                  <th rowSpan={2} style={styles.thLevel}>
                    <span style={styles.thLevelInner}>
                      Level
                      {selectedRows.length > 1 && (
                        <span style={styles.selectedCount}>
                          {selectedRows.length}
                        </span>
                      )}
                    </span>
                  </th>
                  {/* Pedicle Header */}
                  <th colSpan={2} style={styles.thGroup}>Pedicle</th>
                  {/* Vertebral Header */}
                  <th colSpan={3} style={styles.thGroup}>Vertebral</th>
                  {/* Inter-Vertebral Header */}
                  <th colSpan={3} style={styles.thGroup}>Inter-Vertebral</th>
                </tr>
                <tr>
                  <th style={styles.thSubStart}>Width R (mm)</th>
                  <th style={styles.thSub}>Width L (mm)</th>
                  <th style={styles.thSubStart}>Height (mm)</th>
                  <th style={styles.thSub}>Width (mm)</th>
                  <th style={styles.thSub}>Depth (mm)</th>
                  <th style={styles.thSubStart}>Level</th>
                  <th style={styles.thSub}>Height (mm)</th>
                  <th style={styles.thSub}>Angle</th>
                </tr>
              </thead>
              <tbody>
                {measurementsLoading ? (
                  <tr>
                    <td colSpan={9} style={styles.loadingCell}>
                      <div style={styles.loadingIndicator}>
                        <div style={styles.loadingText}>
                          {loadingStatus.action} ({loadingStatus.step}/{loadingStatus.total})
                        </div>
                        <div className="progress" style={styles.loadingProgress}>
                          <div
                            className="progress__fill"
                            style={{
                              width: `${(loadingStatus.step / loadingStatus.total) * 100}%`
                            }}
                          />
                        </div>
                        <div style={styles.skeletonStack}>
                          {[0, 1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="skeleton" style={styles.skeletonRow} />
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  spineData.map((row) => {
                    const getValueStyle = (column, value) => {
                      if (row.hasError) return styles.errorValue;
                      if (isHighlighted(row.level, column, value)) return styles.highlightedValue;
                      return styles.normalValue;
                    };

                    return (
                      <tr
                        key={row.level}
                        style={{
                          ...styles.tableRow,
                          ...(selectedRows.includes(row.level) ? styles.activeTableRow : {}),
                          ...(concerningRows.includes(row.level) && !selectedRows.includes(row.level) ? styles.concerningTableRow :
                            herniatedRows.includes(row.level) && !selectedRows.includes(row.level) ? styles.herniatedTableRow : {}),
                          ...(row.hasError ? styles.errorTableRow : {})
                        }}
                        onClick={(e) => handleRowClick(row.level, e)}
                        onMouseDown={(e) => {
                          // Prevent text selection when using Shift+Click
                          if (e.shiftKey) {
                            e.preventDefault();
                          }
                        }}
                      >
                        {/* Level cell */}
                        <td style={styles.levelCell}>
                          {row.level}
                          {row.hasError && (
                            <span style={styles.levelErrorIndicator}>⚠</span>
                          )}
                        </td>

                        {/* Pedicle measurements */}
                        <td style={styles.dataCellStart}>
                          <span style={getValueStyle('pedicleWidthR', row.pedicleWidthR)}>
                            {row.pedicleWidthR}
                          </span>
                        </td>
                        <td style={styles.dataCell}>
                          <span style={getValueStyle('pedicleWidthL', row.pedicleWidthL)}>
                            {row.pedicleWidthL}
                          </span>
                        </td>

                        {/* Vertebral measurements */}
                        <td style={styles.dataCellStart}>
                          <span style={getValueStyle('vertebralHeight', row.vertebralHeight)}>
                            {row.vertebralHeight}
                          </span>
                        </td>
                        <td style={styles.dataCell}>
                          <span style={getValueStyle('vertebralWidth', row.vertebralWidth)}>
                            {row.vertebralWidth}
                          </span>
                        </td>
                        <td style={styles.dataCell}>
                          <span style={getValueStyle('vertebralDepth', row.vertebralDepth)}>
                            {row.vertebralDepth}
                          </span>
                        </td>

                        {/* Inter-Vertebral measurements */}
                        <td style={styles.dataCellStart}>
                          <span style={styles.interLevelValue}>{row.interWidthL}</span>
                        </td>
                        <td style={styles.dataCell}>
                          <span style={getValueStyle('interHeight', row.interHeight)}>
                            {row.interHeight}
                          </span>
                        </td>
                        <td style={styles.dataCell}>
                          <span style={getValueStyle('interAngle', row.interAngle)}>
                            {row.interAngle}
                          </span>
                        </td>
                      </tr>
                    );
                  }))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MRI Modal */}
      <div
        className="modal-scrim"
        style={{
          display: mriModalOpen ? 'flex' : 'none'
        }}
      >
        <div className="modal" style={styles.mriModalContent}>
          <div className="modal__header">
            <h2 className="modal__title">MRI Analysis</h2>
            <button
              className="icon-btn"
              onClick={() => setMriModalOpen(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="modal__body" style={styles.mriModalBody}>
            <div style={styles.mriImagesRow}>
              {/* Sagittal MRI */}
              <div style={styles.mriImageSection}>
                <div className="card card--tight" style={styles.mriImageCard}>
                  <div style={styles.mriImageHeader}>
                    <h3 style={styles.mriCardTitle}>Sagittal MRI</h3>
                  </div>
                  <div className="viewer" style={styles.mriImageWrapper}>
                    <SagittalMRIViewer
                      selectedLevel={selectedDiscLevel}
                      onCentroidClick={handleSagittalCentroidClick}
                      currentAxialSlice={currentAxialSlice}
                    />
                  </div>
                </div>
              </div>

              {/* Axial MRI */}
              <div style={styles.mriImageSection}>
                <div className="card card--tight" style={styles.mriImageCard}>
                  <div style={styles.mriImageHeader}>
                    <h3 style={styles.mriCardTitle}>Axial MRI</h3>
                  </div>
                  <div className="viewer" style={styles.mriImageWrapper}>
                    <AxialMRIViewer
                      selectedLevel={viewTargetLevel} // Use decoupled target to prevent scroll feedback loop
                      ivdMapping={ivdMapping}
                      onSliceChange={handleAxialSliceChange}
                    />
                  </div>
                </div>
              </div>

              {/* Analysis Panel */}
              <div style={styles.mriAnalysisSection}>
                <div className="card card--tight" style={styles.mriAnalysisCard}>
                  <div style={styles.mriAnalysisHeader}>
                    <h3 style={styles.mriCardTitle}>Analysis for Level</h3>
                    <div style={styles.mriLevelDropdownContainer} id="mri-level-dropdown-container">
                      <div style={styles.mriCustomDropdown}>
                        <div
                          id="mri-level-dropdown-trigger"
                          style={styles.mriDropdownTrigger}
                          onClick={() => {
                            const dropdownContent = document.getElementById('mri-level-dropdown-content');
                            dropdownContent.style.display = dropdownContent.style.display === 'block' ? 'none' : 'block';
                          }}
                        >
                          {selectedDiscLevel}
                          <span style={styles.mriDropdownArrow}>▼</span>
                        </div>
                        <div id="mri-level-dropdown-content" style={styles.mriDropdownContent}>
                          {getDiscLevelsForDropdown().map(level => (
                            <div
                              key={level}
                              style={level === selectedDiscLevel ? { ...styles.mriDropdownItem, ...styles.mriDropdownItemSelected } : styles.mriDropdownItem}
                              onClick={() => {
                                setSelectedDiscLevel(level);
                                // Explicit selection jumps the axial viewer too —
                                // same as clicking a sagittal centroid. Without
                                // this only the analysis panel updated.
                                setViewTargetLevel(level);
                                document.getElementById('mri-level-dropdown-content').style.display = 'none';
                              }}
                              onMouseEnter={(e) => {
                                if (level !== selectedDiscLevel) {
                                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (level !== selectedDiscLevel) {
                                  e.currentTarget.style.backgroundColor = '';
                                }
                              }}
                            >
                              {level}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Diagnosis Summary */}
                  <div style={styles.mriSubCard}>
                    <h4 style={styles.mriSubTitle}>Diagnosis Summary</h4>
                    <div style={styles.mriFindingsGrid}>
                      <div style={styles.mriFindingRow}>
                        <div style={styles.mriFindingLabel}>Disc Herniation:</div>
                        <div className={`badge ${mriAnalysisData[selectedDiscLevel]?.herniation === 'Severe' ? 'badge--danger' :
                          mriAnalysisData[selectedDiscLevel]?.herniation === 'Moderate' ? 'badge--warning' : 'badge--success'}`}>
                          {mriAnalysisData[selectedDiscLevel]?.herniation || 'None'}
                        </div>
                      </div>
                      <div style={styles.mriFindingRow}>
                        <div style={styles.mriFindingLabel}>Stenosis:</div>
                        <div className={`badge ${mriAnalysisData[selectedDiscLevel]?.stenosis ? 'badge--danger' : 'badge--success'}`}>
                          {mriAnalysisData[selectedDiscLevel]?.stenosis ? 'Present' : 'None'}
                        </div>
                      </div>
                      <div style={styles.mriFindingRow}>
                        <div style={styles.mriFindingLabel}>Nerve Compression:</div>
                        <div style={styles.mriNerveCompressionBadges}>
                          <div className={`badge ${mriAnalysisData[selectedDiscLevel]?.nerveCompression?.left ? 'badge--danger' : 'badge--success'}`}>
                            L: {mriAnalysisData[selectedDiscLevel]?.nerveCompression?.left ? 'Yes' : 'No'}
                          </div>
                          <div className={`badge ${mriAnalysisData[selectedDiscLevel]?.nerveCompression?.right ? 'badge--danger' : 'badge--success'}`}>
                            R: {mriAnalysisData[selectedDiscLevel]?.nerveCompression?.right ? 'Yes' : 'No'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Measurements */}
                  <div style={styles.mriSubCard}>
                    <h4 style={styles.mriSubTitle}>Measurements</h4>
                    <div style={styles.mriMeasurementsTable}>
                      <div style={styles.mriMeasurementRow}>
                        <div style={styles.mriMeasurementName}>Left Foraminal Depth</div>
                        <div style={styles.mriMeasurementValue}>{mriAnalysisData[selectedDiscLevel]?.foraminalDepthLeft || '-'} mm</div>
                      </div>
                      <div style={styles.mriMeasurementRow}>
                        <div style={styles.mriMeasurementName}>Right Foraminal Depth</div>
                        <div style={styles.mriMeasurementValue}>{mriAnalysisData[selectedDiscLevel]?.foraminalDepthRight || '-'} mm</div>
                      </div>
                      <div style={styles.mriMeasurementRow}>
                        <div style={styles.mriMeasurementName}>Central Canal Area</div>
                        <div style={styles.mriMeasurementValue}>{mriAnalysisData[selectedDiscLevel]?.centralCanalArea || '-'} mm²</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-save confirmation (study saved when processing completed) */}
      {autoSaveNotice && (
        <div className="toast-region">
          <div className="toast">
            {autoSaveNotice === 'saved' ? (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="var(--success, #34c77b)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Scan saved to history</span>
              </>
            ) : (
              <span>Automatic save failed — you can save manually from Scan History</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Static inline styles: only layout glue and truly dynamic/conditional values.
// All interactive states (hover/active/disabled) come from global.css classes.
const styles = {
  container: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  dashboard: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    minHeight: 0,
    gap: 'var(--space-4)',
    width: '100%',
    padding: 'var(--space-4) clamp(var(--space-4), 2.5vw, var(--space-6)) var(--space-4)',
  },

  // Left Section - Spine Model & Controls
  // height 100%: the dashboard is a flex ROW, and rows never shrink children
  // on the cross axis — without a definite height the 3D viewer's intrinsic
  // size stretches the whole line past the viewport and the page scrolls
  // instead of the measurements panel.
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 380px',
    minWidth: '280px',
    minHeight: 0,
    height: '100%',
    gap: 'var(--space-3)',
  },
  viewingCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flexShrink: 0,
  },
  viewingHead: {
    gap: 'var(--space-2)',
    flexWrap: 'nowrap',
  },
  panelLabel: {
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    color: 'var(--text-secondary)',
  },
  segFull: {
    display: 'flex',
    width: '100%',
  },
  segOptFlex: {
    flex: 1,
  },
  viewerBox: {
    flex: '1 1 auto',
    minHeight: '280px',
  },
  regionCard: {
    display: 'flex',
    gap: 'var(--space-2)',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  regionButton: {
    flex: '1 1 auto',
  },
  regionButtonActive: {
    background: 'var(--accent-soft)',
    boxShadow: 'inset 0 0 0 1px rgba(79, 156, 240, 0.35)',
    color: 'var(--accent-hover)',
  },
  regionButtonRequired: {
    cursor: 'not-allowed',
  },

  // Right Section - Toolbar & Data Table
  rightSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 560px',
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    gap: 'var(--space-3)',
  },
  toolbar: {
    gap: '10px',
    flexShrink: 0,
  },
  selectionInfo: {
    marginLeft: 'auto',
    height: 'auto',
    padding: '5px 12px',
  },
  selectionInfoText: {
    fontSize: '0.75rem',
    fontWeight: 500,
    letterSpacing: 0,
    whiteSpace: 'nowrap',
    color: 'var(--text-secondary)',
  },

  // Measurements table
  tableScroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    background: 'var(--bg-surface)',
  },
  measurementTable: {
    minWidth: '760px',
    // Collapsed borders don't travel with sticky header cells, which left a
    // 1px seam between the group row and sub row where data showed through.
    // Separate borders belong to the cells and stick with them.
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  thLevel: {
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  thLevelInner: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectedCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '18px',
    height: '18px',
    padding: '0 5px',
    marginLeft: '7px',
    borderRadius: 'var(--radius-full)',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: 0,
  },
  thGroup: {
    textAlign: 'center',
    height: '34px',
    padding: '6px 10px',
    borderLeft: '1px solid var(--line-subtle)',
    color: 'var(--text-secondary)',
  },
  // Both header rows are exactly 34px (border-box) so the sub row pins flush
  // at top: 34px with no gap, and the auto-hide transform can move the whole
  // 68px header out of view.
  thSub: {
    top: '34px',
    height: '34px',
    padding: '6px 10px',
    textAlign: 'center',
  },
  thSubStart: {
    top: '34px',
    height: '34px',
    padding: '6px 10px',
    textAlign: 'center',
    borderLeft: '1px solid var(--line-subtle)',
  },
  tableRow: {
    cursor: 'pointer',
    userSelect: 'none', // Prevent text selection
    WebkitUserSelect: 'none', // For Safari
    msUserSelect: 'none', // For IE/Edge
    MozUserSelect: 'none', // For Firefox
  },
  activeTableRow: {
    background: 'var(--accent-soft)',
  },
  concerningTableRow: {
    background: 'rgba(240, 86, 79, 0.08)',
  },
  herniatedTableRow: {
    background: 'rgba(245, 184, 61, 0.08)',
  },
  errorTableRow: {
    background: 'rgba(240, 86, 79, 0.04)',
  },
  levelCell: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    padding: '9px 10px',
  },
  levelErrorIndicator: {
    marginLeft: '5px',
    color: 'var(--danger)',
    fontSize: '0.8rem',
  },
  dataCell: {
    textAlign: 'center',
    padding: '9px 10px',
  },
  dataCellStart: {
    textAlign: 'center',
    padding: '9px 10px',
    borderLeft: '1px solid var(--line-subtle)',
  },
  normalValue: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.84rem',
    color: 'var(--text-primary)',
  },
  highlightedValue: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.84rem',
    color: 'var(--danger)',
    fontWeight: 600,
  },
  errorValue: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.84rem',
    color: 'var(--danger)',
    fontStyle: 'italic',
  },
  interLevelValue: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.84rem',
    color: 'var(--text-secondary)',
  },

  // Loading state (progress + skeleton rows)
  loadingCell: {
    padding: 0,
    borderBottom: 'none',
  },
  loadingIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    padding: '28px 24px',
  },
  loadingText: {
    fontSize: '0.88rem',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    textAlign: 'center',
  },
  loadingProgress: {
    maxWidth: '420px',
  },
  skeletonStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
    marginTop: '6px',
  },
  skeletonRow: {
    height: '34px',
  },

  // MRI Modal
  mriModalContent: {
    maxWidth: '1400px',
    height: '90vh',
    maxHeight: '90vh',
  },
  mriModalBody: {
    flex: 1,
    minHeight: 0,
    padding: 'var(--space-4)',
  },
  mriImagesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: 'var(--space-4)',
    height: '100%',
  },
  mriImageSection: {
    flex: '1 1 320px',
    minWidth: 0,
    display: 'flex',
  },
  mriAnalysisSection: {
    flex: '1 1 300px',
    maxWidth: '440px',
    minWidth: 0,
    display: 'flex',
  },
  mriImageCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: '380px',
  },
  mriImageHeader: {
    marginBottom: '10px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--line-subtle)',
  },
  mriCardTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
    margin: 0,
  },
  mriImageWrapper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mriAnalysisCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gap: 'var(--space-3)',
  },
  mriAnalysisHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-2)',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--line-subtle)',
  },
  mriLevelDropdownContainer: {
    marginLeft: 'var(--space-2)',
  },
  mriCustomDropdown: {
    position: 'relative',
    display: 'inline-block',
    minWidth: '96px',
    zIndex: 100,
  },
  mriDropdownTrigger: {
    background: 'var(--bg-overlay)',
    color: 'var(--text-primary)',
    border: '1px solid var(--line-strong)',
    borderRadius: 'var(--radius-sm)',
    padding: '0 12px',
    height: '34px',
    fontSize: '0.86rem',
    fontWeight: 500,
    minWidth: '96px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  mriDropdownArrow: {
    fontSize: '9px',
    color: 'var(--text-tertiary)',
  },
  mriDropdownContent: {
    display: 'none',
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    minWidth: '100%',
    background: 'var(--bg-overlay)',
    border: '1px solid var(--line-default)',
    borderRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 101,
    maxHeight: '250px',
    overflowY: 'auto',
  },
  mriDropdownItem: {
    color: 'var(--text-primary)',
    padding: '9px 12px',
    display: 'block',
    cursor: 'pointer',
    fontSize: '0.86rem',
    borderBottom: '1px solid var(--line-subtle)',
  },
  mriDropdownItemSelected: {
    backgroundColor: 'var(--accent-soft)',
    color: 'var(--accent-hover)',
    fontWeight: 600,
  },
  mriSubCard: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid var(--line-subtle)',
    borderRadius: 'var(--radius-sm)',
    padding: 'var(--space-3)',
  },
  mriSubTitle: {
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    color: 'var(--text-secondary)',
    margin: '0 0 10px 0',
  },
  mriFindingsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  mriFindingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-2)',
  },
  mriFindingLabel: {
    fontSize: '0.86rem',
    color: 'var(--text-secondary)',
  },
  mriNerveCompressionBadges: {
    display: 'flex',
    gap: '6px',
  },
  mriMeasurementsTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  mriMeasurementRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-2)',
  },
  mriMeasurementName: {
    fontSize: '0.86rem',
    color: 'var(--text-secondary)',
  },
  mriMeasurementValue: {
    fontSize: '0.86rem',
    fontWeight: 600,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
  },
};

// Add simple spin animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default AnatomicalMeasurementsPage;
